import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ElementRef,
  Inject,
  Input,
  ViewChild,
} from "@angular/core";
import { lastValueFrom, Observable, Subject } from "rxjs";
import { Router, ActivatedRoute, ParamMap } from "@angular/router";
import {
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
  MatDialogConfig as MatDialogConfig,
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
} from "@angular/material/dialog";

import { Emis } from "../../_constants/Emis";
import { TemplateService } from "../service/template.service";
import {
  TemplateContent,
  TemplateRulebooks,
  Rb,
  SectContents,
  ParaContents,
  ReviewDialogData,
} from "../model/TemplateRulebook";
import { AlertifyService } from "../../_models/alertify.service";
import { HelpInfoService } from "../../_common/help-info.service";
import { AuthService } from "../../auth/auth.service";
import { Account } from "../../_models";
import { MatRadioChange as MatRadioChange } from "@angular/material/radio";
import { AnswerInputType } from "@microsoft/microsoft-graph-types";
import { diffHtml } from "../shared/text-diff.util";

/**
 *
 */
declare let alertify: any;
@Component({
  selector: "app-edit-template",
  templateUrl: "./edit-template.component.html",
  styleUrls: ["./edit-template.component.css"],
  standalone: false,
})
export class EditTemplateComponent implements OnInit {
  csskey: string;
  selectedAcronym: string;
  auditName: string;
  percentage: string;
  account: Account;
  facilityLocation: string;
  auditDate: string;

  rulebooks: TemplateContent[];
  auditPrint$ = new Subject<TemplateContent[]>();
  emisid = 0; //EMIS parter id
  editable = true;
  isDualLanguage = false; // Indicate if the template is dual language.

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public service: TemplateService,
    private alertifyService: AlertifyService,
    private cdr: ChangeDetectorRef,
    public dialog: MatDialog,
    private helpInfoService: HelpInfoService,
  ) {}

  ngOnInit() {
    this.route.paramMap.subscribe((paraMap) => {
      this.csskey = paraMap.get("id");
    });
    this.account = AuthService.currentUser;
    this.auditName = this.service.getAuditName();
    this.facilityLocation = this.service.getFacilityLocation();
    this.auditDate = this.service.getAuditDate();
    this.emisid = this.account.emis ?? 0;
    this.service.updateTemplateDateStorage(+this.csskey); // update its cssdate in the session storage.
    this.editable = this.service.isTemplateEditable(+this.csskey);
    this.isDualLanguage = this.service.isDualLanguageTemplate();
    // Check if the rulebooks of this template have been cached.
    let cached = this.service.getRulebookLocalStorage(parseInt(this.csskey));
    if (cached != null && cached.length > 0) {
      this.service.touchTemplate(this.csskey);
      this.populateRulebook(cached);
    } else {
      this.service.showLoadingAnimation(true);
      this.service.getContent(+this.csskey).subscribe(
        (res) => {
          this.service.showLoadingAnimation(false);
          this.service.cacheRulebookLocalStorage(parseInt(this.csskey), res);
          this.populateRulebook(res);
        },
        (err) => {
          console.error("got an error:" + err.error);
          this.service.showLoadingAnimation(false);
          this.alertifyService.error(err.error);
        },
      );
    }
  }

  reload() {
    this.service.removeTemplateLocalStorage(+this.csskey);

    this.service.showLoadingAnimation(true);
    this.service.getContent(+this.csskey).subscribe(
      (res) => {
        this.service.showLoadingAnimation(false);
        this.service.cacheRulebookLocalStorage(parseInt(this.csskey), res);
        this.populateRulebook(res);
      },
      (err) => {
        console.error("got an error:" + err.error);
        this.service.showLoadingAnimation(false);
        this.alertifyService.error(err.error);
      },
    );
  }

  getEmis(): string {
    if (this.emisid == 0) return "";
    const found = Emis.find((element) => element.value == this.emisid);
    if (found === undefined) return "";
    return found.name;
  }

  getEmisBuildType(): string {
    if (this.emisid == 0) return "";
    const found = Emis.find((element) => element.value == this.emisid);
    if (found === undefined) return "";
    return found.buildType;
  }

  getDemoBuildType(email: string): string {
    if (email.toLowerCase() == "processmap-demo@stpub.com")
      return "Ideagen EHS";
    else return email.substring(0, email.indexOf("-demo"));
  }

  backToHome() {
    this.router.navigateByUrl("/audithub/apptool/list");
  }

  // "88.8%"" -> 0.888
  convertPercentage(percentage: string) {
    if (percentage) {
      return parseFloat(percentage.replace("%", "")) / 100;
    }
    return null;
  }

  // Populate the page with rulebook data of the tempalte.
  populateRulebook(res: TemplateContent[]): void {
    var questionSumTemplate = 0;
    var answeredSumTemplate = 0;

    // Set the completing percentage of rulebook, acronym and template at the frontend, not the backend.
    res.forEach((acronym) => {
      var questionSumAcronym = 0;
      var answeredSumAconrym = 0;

      acronym.topics.forEach((tp) => {
        tp.topic = this.service.decodeHtml(tp.topic) ?? "";
        tp.rulebooks.forEach((rb) => {
          let { sum, answered } = this.service.calQuestionSum(rb.appAnswer);
          rb.percentage = this.service.calPercentage(answered, sum);
          rb.rbName = this.service.decodeHtml(rb.rbName) ?? "";
          rb.checkNa = rb.checkNa
            ? rb.checkNa
            : this.service.calCheckNa(rb.appAnswer);
          rb.status = this.checkRulebookStatus(rb);
          if (rb.checkNa == false) {
            // We do not count in rulebooks marked as CheckNA
            questionSumAcronym += sum;
            answeredSumAconrym += answered;
          }
          if (rb.abridged) {
            // For abridged rulebooks, making fake question and answer to reflect the percentage=> 1/1 = 100%
            questionSumAcronym++;
            answeredSumAconrym++;
          }
        });
      });

      if (questionSumAcronym == 0) {
        acronym.percentage = "N/A";
      } else {
        acronym.percentage = this.service.calPercentage(
          answeredSumAconrym,
          questionSumAcronym,
        );
      }
      questionSumTemplate += questionSumAcronym;
      answeredSumTemplate += answeredSumAconrym;
    });
    this.percentage = this.service
      .calPercentage(answeredSumTemplate, questionSumTemplate)
      .replace("%", "");
    this.rulebooks = res;
    this.selectedAcronym = res[0].acronym;
    this.service.setCurrent(res);
  }

  // click on button print audit
  printAudit() {
    this.service.showLoadingAnimation(true);
    this.service.getAuditPrint(+this.csskey).subscribe(
      (res) => {
        this.service.showLoadingAnimation(false);
        for (let acms of res) {
          for (let tps of acms.topics) {
            for (let rbs of tps.rulebooks) {
              rbs.rbName = this.service.decodeHtml(rbs.rbName) ?? "";
              for (let apps of rbs.appSect) {
                apps.question = this.service.decodeHtml(apps.question);
                apps.exemption = this.service.decodeHtml(apps.exemption);
              }
            }
          }
        }
        this.auditPrint$.next(res);
        this.cdr.detectChanges(); // Force refreshing the page to load the data for printing.
      },
      (err) => {
        this.service.showLoadingAnimation(false);
        console.error("got an error:" + err);
      },
      () => {
        setTimeout(() => {
          const content =
            document.getElementById("audit-print")?.innerHTML ?? "";
          const printWindow = window.open("", "_blank");
          if (!printWindow) return;
          const html = `<!DOCTYPE html><html><head><title>Audit Print</title><style>
            body { font-family: "Helvetica Neue", sans-serif; }
            h2 { text-align: center; }
            div.table { padding-left: 5px; }
            div.table-header { border-bottom: 1px solid black; display: flex; justify-content: space-between; }
            div.row-end { padding-right: 25px; text-align: right; }
            div.table-body { padding-top: 10px; }
            div.table-body .row-fluid { border-bottom: 1px dotted lightgray; display: inline-block; width: 100%; }
          </style></head><body>${content}
          <script>window.onload = function() { window.print(); window.addEventListener('afterprint', function() { window.close(); }); };</script>
          </body></html>`;
          printWindow.onload = () => {
            this.service.showLoadingAnimation(false);
            console.log("end print");
          };
          printWindow.document.open();
          printWindow.document.write(html);
          printWindow.document.close();
        });
      },
    );
  }

  // Return the first applicable rulebook id of this template.
  getFirstApplicableRulebook(): number {
    const rulebook = this.rulebooks
      .flatMap((rb) => rb.topics)
      .flatMap((tp) => tp.rulebooks)
      .find((rbk) => rbk.checkNa === false);
    return rulebook ? rulebook.rbKey : -1;
  }

  // Determine the status of a rulebook according to its applicability answers
  checkRulebookStatus(rb: Rb) {
    if (rb.needsReview) {
      return "Needs Review";
    }

    if (rb.checkNa) {
      return "Not Applicable";
    }

    if (rb.done || this.isDone(rb)) {
      return "Applicable";
    } else {
      return "Incomplete";
    }
  }

  // To determine if the questions are all answered.
  isDone(rb: Rb): boolean {
    if (!rb.appAnswer) return false;
    for (var x = 0; x < rb.appAnswer.length; x++) {
      let c = rb.appAnswer.charAt(x);

      if (c == "I")
        // unanswered question
        return false;
      if (c == "i" && rb.appAnswer.charAt(x - 1) == "Y")
        // unanswered excemption and the answer to the question is Yes.
        return false;
    }
    return true;
  }

  onAcronymChange(acronym: string) {
    this.selectedAcronym = acronym;
  }

  onRbClick(appkey: number, rbName: string, abridged: boolean) {
    if (abridged) {
      this.alertifyService.confirmOk(
        "Abridged Protocols do not have applicability questions. The rulebook questions will be included in your Xcelerator automatically.",
      );
      return false;
    }
    this.changeRulebook(rbName);
    this.getApplicability(appkey);
  }

  removeCheckUpdatFlag(csskey: number, appkey: number) {
    var myurl = "";
  }

  onAddRulebook(event) {
    var myurl = "/audithub/apptool/new/" + this.csskey;
    this.router.navigateByUrl(myurl).then(
      (nav) => {
        console.log("Navigate to: " + myurl);
      },
      (err) => {
        console.log(err);
      },
    );
  }

  // change event on checkbox of Check N/A on rulebook
  onCheckNaChange(event, appkey: number) {
    this.service.showLoadingAnimation(true);
    this.service.checkNa(+this.csskey, appkey, event.checked).subscribe(
      (res) => {
        this.updateRulebookStatus(appkey, event.checked);
      },
      (err) => console.error("got an error:" + err),
      () => {
        this.service.showLoadingAnimation(false);
      },
    );
    let theMirroredAppkey = this.getMirroredAppkey(appkey);
    if (theMirroredAppkey === -1) return;

    this.service.showLoadingAnimation(true);
    this.service
      .checkNa(+this.csskey, theMirroredAppkey, event.checked)
      .subscribe(
        (res) => {
          this.updateRulebookStatus(theMirroredAppkey, event.checked);
        },
        (err) => console.error("got an error:" + err),
        () => {
          this.service.showLoadingAnimation(false);
        },
      );
  }

  /**
   * Get the mirrored appkey of the rulebook.
   * @param appkey: the appkey of the rulebook.
   * @return: the mirrored appkey if found, otherwise return -1.
   */
  getMirroredAppkey(appkey: number): number {
    if (!this.isDualLanguage) return -1;
    for (const acronym of this.rulebooks) {
      for (const topic of acronym.topics) {
        const rb = topic.rulebooks.find((rb) => rb.appKey === appkey);
        if (rb) {
          const mirroredAcronym = this.service.getDualLanguageAcronym(
            acronym.acronym,
          );
          if (!mirroredAcronym) return -1;
          const mirroredPub = this.rulebooks.find(
            (a) =>
              a.acronym === mirroredAcronym &&
              a.releaseNum === acronym.releaseNum,
          );
          if (!mirroredPub) return -1;
          const mirroredTopic = mirroredPub.topics.find(
            (t) => t.topic === topic.topic,
          );
          if (!mirroredTopic || !mirroredTopic.rulebooks.length) return -1;
          return mirroredTopic.rulebooks[0].appKey;
        }
      }
    }
    return -1;
  }

  /**
   * Review the section and paragraph difference under a rulebook after the template updated from one version to another.
   * @param event
   * @param appkey
   * @param sigOnly when true, only show paragraphs marked as a significant change
   */
  onReviewUpdate(event: any, appkey: number, sigOnly: boolean = false) {
    event.stopPropagation();
    this.service.showLoadingAnimation(true);
    this.service.reviewUpdate(parseInt(this.csskey), appkey, sigOnly).subscribe(
      (res) => {
        this.service.showLoadingAnimation(false);
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = {
          sections: res,
          sigOnly: sigOnly,
          csskey: parseInt(this.csskey),
          appkey: appkey,
        };
        dialogConfig.width = "1200px";
        // Anchor the dialog to a fixed top offset instead of letting MatDialog
        // recenter it - height still auto-fits content, so switching between
        // the two toggle views resizes the dialog without it jumping position.
        dialogConfig.position = { top: "5vh" };
        const dialogRef = this.dialog.open(DisplayReviewDialog, dialogConfig);

        dialogRef.afterClosed().subscribe((result) => {
          if (result) {
            // Remove the review flag.
            // Update local cache, set 'checkUpdate = false' for this rulebook.
            this.service.revokeReviewFlag(parseInt(this.csskey), appkey, 2);
            //Refresh the page to not display the flag
            let cached = this.service.getRulebookLocalStorage(
              parseInt(this.csskey),
            );
            if (cached != null && cached.length > 0) {
              this.service.touchTemplate(this.csskey);
              this.populateRulebook(cached);
            }
            // Call backend to remove the 'checkUpdate' flag
            this.service.removeCheckUpdateFlag(+this.csskey, appkey).subscribe(
              (res) => {
                console.log("success.");
              },
              (err) => {
                console.log("backend return error: " + err);
                this.alertifyService.error(err.error);
              },
            );
          }
        });
      },
      (err) => {
        console.error(err);
        this.service.showLoadingAnimation(false);
        this.alertifyService.error(err.error);
      },
    );
  }

  // After call backend to set flag of checkNa, update the view
  updateRulebookStatus(appkey: number, checkNa: boolean) {
    // Update only the matching rulebook
    const rb = this.rulebooks
      .flatMap((acronym) => acronym.topics)
      .flatMap((topic) => topic.rulebooks)
      .find((rb) => rb.appKey === appkey);
    if (rb) {
      rb.checkNa = checkNa;
      rb.appAnswer = this.getAnswerAfterCheckNa(checkNa, rb.appAnswer);
    }
    this.updateTemplatePercentage();
  }

  /**
   * Update the answer of the applicability question after checking the N/A checkbox.
   * @param checked: turn on or off the N/A checkbox.
   * @param answer: the answer of the applicability question.
   * @returns {string} the updated answer of the applicability question.
   */
  getAnswerAfterCheckNa(checked: boolean, answer: string): string {
    if (answer == null || answer == undefined) {
      return "";
    }
    if (checked) {
      // If the N/A checkbox is checked, set the rulebook answer to "N", and section answer to initial value: "I";
      for (let i = 0; i < answer.length; i++) {
        if (i == 0) {
          // Set the first answer to N - the rulebook answer.
          answer = "N" + answer.substring(1);
        } else if (answer.charAt(i) === answer.charAt(i).toLowerCase()) {
          // If the answer is exemption, set it to i - unanswered.
          answer = answer.substring(0, i) + "i" + answer.substring(i + 1);
        } else {
          // Set the rest of the answers to "I" - unanswered.
          answer = answer.substring(0, i) + "I" + answer.substring(i + 1);
        }
      }
    } else {
      // If the N/A checkbox is unchecked, set the rulebook answer to "Y" - Yes.
      answer = "Y" + answer.substring(1);
      // If the second answer is for an exemption, set it to "n" - no exemption.
      if (
        answer.length > 1 &&
        answer.charAt(1) === answer.charAt(1).toLowerCase()
      ) {
        answer = answer.substring(0, 1) + "n" + answer.substring(2);
      }
    }
    return answer;
  }

  /**
   * Update the completing percentage of the template.
   * @returns void
   */
  updateTemplatePercentage(): void {
    let questionSumInTemplate = 0;
    let answeredSumInTemplate = 0;

    this.rulebooks.forEach((acronym) => {
      let questionSumInAcronym = 0;
      let answeredSumInAconrym = 0;

      acronym.topics.forEach((tp) => {
        tp.topic = this.service.decodeHtml(tp.topic) ?? "";

        // Loop through all rulebooks to update the percentage and status
        tp.rulebooks.forEach((rb) => {
          const { sum, answered } = this.service.calQuestionSum(rb.appAnswer);
          rb.percentage =
            rb.checkNa === false
              ? this.service.calPercentage(answered, sum)
              : "N/A";
          rb.rbName = this.service.decodeHtml(rb.rbName) ?? "";
          rb.status = this.checkRulebookStatus(rb);
          if (rb.checkNa === false) {
            questionSumInAcronym += sum;
            answeredSumInAconrym += answered;
          }
        });
      });

      acronym.percentage =
        questionSumInAcronym === 0
          ? "N/A"
          : this.service.calPercentage(
              answeredSumInAconrym,
              questionSumInAcronym,
            );

      questionSumInTemplate += questionSumInAcronym;
      answeredSumInTemplate += answeredSumInAconrym;
    });

    this.service.cacheRulebookLocalStorage(
      parseInt(this.csskey),
      this.rulebooks,
    );
    this.percentage = this.service
      .calPercentage(answeredSumInTemplate, questionSumInTemplate)
      .replace("%", "");
  }

  // To check if the template contains protocols that might have state difference.(Only EAF or OF has SD)
  hasStateDifference(): boolean {
    let permission = sessionStorage.getItem("auditHubPermissions");
    if (permission == null || permission == undefined) return false;

    let eafSd = JSON.parse(permission).eaf;
    let ofSd = JSON.parse(permission).of;

    for (var i = 0; i < this.rulebooks.length; i++) {
      if (
        this.rulebooks[i].acronym == "EAF" &&
        eafSd &&
        eafSd.trim().length > 0
      ) {
        return true;
      }
      if (this.rulebooks[i].acronym == "OF" && ofSd && ofSd.trim().length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Open the dialog to display help information.
   * @param $event: click mouse event
   * @param index: the index of the help info. See ./_constants/AppSettting for more detail.
   */
  openHelpInfo($evt: MouseEvent, index: number): void {
    this.helpInfoService.open($evt, index, { width: 350 });
  }

  buildXcelerator(buildType: string, answer: string, states?: string[]) {
    try {
      this.validateBeforeBuild();
      if (buildType.toLowerCase() == "no") {
        this.alertifyService.confirmOk(
          "<p>Your audit template has been sent to your EHS MIS provider.  STP audit template conversions take 1-2 days.  Your EHS MIS provider may also need time to integrate your template.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p>",
        );
        return;
      }
      this.service.showLoadingAnimation(true);
      this.service
        .buildXcelerator(+this.csskey, buildType, answer, states)
        .subscribe(
          (res) => {
            this.service.showLoadingAnimation(false);
            if (buildType === "X3.0" || buildType === "X3.1") {
              this.alertifyService.confirmOk(
                "<p>Xcelerator file can take a while to prepare.</p><p>Once it's completed we'll send you an email at: " +
                  this.service.getLoginEmail() +
                  "</p>",
              );
            } else if (buildType === "C3.0") {
              this.alertifyService.confirmOk(
                "<p>Applicable Citations file can take a while to prepare.</p><p>Once it's completed we'll send you an email at: " +
                  this.service.getLoginEmail() +
                  "</p>",
              );
            } else if (buildType === "Vector") {
              this.alertifyService.confirmOk(
                "<p>Vector file can take a while to prepare.</p><p>Once it's completed we'll send you an email at: " +
                  this.service.getLoginEmail() +
                  "</p>",
              );
            } else if (buildType === "reghub") {
              this.service.markReghubPending(+this.csskey);
              this.alertifyService.confirmOk(
                "<p>RegRegister monitor has been set for the template.</p>",
              );
            } else if (buildType === "csv") {
              this.alertifyService.confirmOk(
                "<p>CSV file can take a while to prepare.</p><p>Once it's completed we'll send you an email at: " +
                  this.service.getLoginEmail() +
                  "</p>",
              );
            } else if (buildType.toLowerCase() == "intelex") {
              this.alertifyService.confirmOk(
                "<p>Your audit template has been sent to Intelex. You will receive a confirmation e-mail once the template is ready.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p> <img src='../../../assets/images/intelex-logo.png'>",
              );
            } else if (buildType.toLowerCase() == "safetystr") {
              this.alertifyService.confirmOk(
                "<p>Your audit template has been sent to SafetyStratus for processing.  The audit template conversion and integration will take approximately 24 hours.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p><img src='../../../assets/images/safetystr-logo.svg'>",
              );
            } else if (buildType.toLowerCase() == "velocity") {
              this.alertifyService.confirmOk(
                "<p>Your audit template is now in the formatting queue, and you will receive a confirmation email once the file is ready.</p><p>VelocityEHS may need up to 5 days to integrate your template.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p><br><img class='velocity-bg' src='../../../assets/images/velocityehs-logo.svg'>",
              );
            } else if (buildType.toLowerCase() == "processmap") {
              this.alertifyService.confirmOk(
                "<p>Your audit template is now in the formatting queue, and you will receive a confirmation email once the file is ready.</p><p>Ideagen EHS may need 2-3 days to integrate your template.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p><br><img src='../../../assets/images/processmap-logo.png'>",
              );
            } else {
              this.alertifyService.confirmOk(
                "<p>Your audit template has been sent to your EHS MIS provider.  STP audit template conversions take 1-2 days.  Your EHS MIS provider may also need time to integrate your template.</p><p>Please contact us at <b>info@stpub.com</b> if you have any questions.</p>",
              );
            }
          },
          (err) => {
            this.service.showLoadingAnimation(false);
            if (err.status === 400) {
              this.alertifyService.confirmOk(
                `<b><span style='color: red;'>Warning</span></b>: based on the applicability responses provided, this template contains no relevant ${err.error ? `federal or state [${err.error}]` : ""} requirements; therefore, <b>we are unable to complete this request.</b>`,
              );
            } else {
              this.alertifyService.error(err.error);
            }
          },
          () => {
            this.service.showLoadingAnimation(false);
          },
        );
    } catch (Error) {
      this.service.showLoadingAnimation(false);
      this.alertifyService.error(Error.message);
    }
  }

  // Event listener on button 'build'
  async onBuild(event, buildType: string) {
    if (!isNaN(parseInt(buildType))) {
      buildType = this.getEmisBuildType();
    }
    var answer = "";
    if (buildType.toLowerCase() === "intelex") {
      if (this.service.getIntelexFlag()) {
        this.alertifyService.confirmOk(
          "You have already sent this template to Intelex.  Request ignored.",
        );
        return;
      }
      try {
        this.service.showLoadingAnimation(true);
        var answers = await this.getAnswerList();
        this.service.showLoadingAnimation(false);
        const result = await this.openPreAnswerDialog(answers);

        if (result === null) {
          return;
        }
        answer = result;
      } catch (error) {
        this.alertifyService.error(
          "Could not get the answer list from Intelex. Please try again later.",
        );
        this.service.showLoadingAnimation(false);
        return;
      }
    }
    if (buildType.toLowerCase() == "safetystratus") buildType = "SAFETYSTR";

    if (this.hasStateDifference()) {
      let state = this.service.getFavoriteState(this.csskey);

      const authorized_state = this.getAuthorizedState();
      if (authorized_state.length > 0) {
        const result = await this.openStateListDialog(
          state,
          buildType,
          authorized_state,
        );
        if (result === null) {
          return false;
        }
        if (result.length > 0) {
          if (
            result[result.length - 1] == "0" ||
            result[result.length - 1] == "1"
          ) {
            // Xcelerator unlock/lock
            if (result[result.length - 1] == "1") {
              buildType = "X3.1";
            }
            result.pop();
          }
          if (result.length > 0) {
            this.service.cacheFavoriteState(this.csskey, result[0]); // Save the first state as the favoriate for the future useage.
            this.buildXcelerator(buildType, answer, result);
            return false;
          }
        }
        this.buildXcelerator(buildType, answer);
      }
    } else {
      const result = await this.openConfirmDialog(buildType);

      if (result === -1) {
        return false;
      }
      if (result < 2) {
        this.buildXcelerator(buildType, answer);
        return false;
      } else {
        this.buildXcelerator("X3.1", answer);
      }
    }
  }

  getAuthorizedState(): string[] {
    var authorized_state: string[] = [];
    let permission = sessionStorage.getItem("auditHubPermissions");
    if (permission == null || permission == undefined) return authorized_state;

    let eafSd = JSON.parse(permission).eaf;
    let ofSd = JSON.parse(permission).of;

    if (eafSd && eafSd.trim().length > 0) {
      authorized_state = authorized_state.concat(
        eafSd.toUpperCase().split(","),
      );
    }
    if (ofSd && ofSd.trim().length > 0) {
      authorized_state = authorized_state.concat(ofSd.toUpperCase().split(","));
    }
    if (authorized_state.length == 0) return authorized_state;
    return authorized_state.filter((v, i, a) => a.indexOf(v) === i).sort();
  }

  async openConfirmDialog(buildType: string): Promise<number> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = "480px";
    dialogConfig.panelClass = "confirm-dialog";
    dialogConfig.data = {
      build: buildType,
    };

    const dialogRef = this.dialog.open(ConfirmBuildDialog, dialogConfig);
    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => {
        return Promise.resolve(result);
      });
  }

  async openPreAnswerDialog(answers: string[]): Promise<string> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      answers: answers,
    };
    dialogConfig.width = "800px";
    const dialogRef = this.dialog.open(PreAnswerListDialog, dialogConfig);
    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => {
        return Promise.resolve(result);
      });
  }

  async openStateListDialog(
    state: string,
    buildType: string,
    authorized_state: string[],
  ): Promise<string[]> {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.panelClass = "confirm-dialog";
    dialogConfig.data = {
      selected: state || "",
      build: buildType,
      states: authorized_state,
    };
    dialogConfig.width = "800px";
    const dialogRef = this.dialog.open(StateListDialog, dialogConfig);

    return dialogRef
      .afterClosed()
      .toPromise()
      .then((result) => {
        return Promise.resolve(result);
      });
  }

  async getAnswerList() {
    const source$ = this.service.getIntelexAnswer();
    var response = await lastValueFrom(source$);
    if (response) {
      return response.answer;
    }
  }

  // on clicking button 'View Rulebooks'
  onViewRulebooks() {
    console.log("on View Rulebook.");
    let rbkey = this.getFirstApplicableRulebook();

    if (rbkey !== null) {
      var myurl =
        "/audithub/apptool/viewRulebooks/" +
        this.csskey +
        "/" +
        rbkey +
        "|" +
        this.encodeUriAll(this.auditName);
      this.router.navigateByUrl(myurl).then(
        (nav) => {
          console.log("Navigate to: " + myurl);
        },
        (err) => {
          console.log(err);
        },
      );
    } else {
      alert("There is no applicable rulebook to view!");
    }
  }

  encodeUriAll(value: string): string {
    return value.replace(
      /[^A-Za-z0-9]/g,
      (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
    );
  }

  // on clicking button 'View RegRegiser'
  onViewRegRegister() {
    var myurl =
      "/audithub/apptool/viewCitations/" +
      this.csskey +
      "|" +
      this.encodeUriAll(this.auditName);
    this.router.navigateByUrl(myurl).then(
      (nav) => {
        console.log("Navigate to: " + myurl);
      },
      (err) => {
        console.log(err);
      },
    );
  }

  validateBeforeBuild(): boolean {
    if (this.csskey != "23") return true;
    throw new Error("cannot build.");
  }

  changeRulebook(rbName: string) {
    this.service.setRbName(rbName);
  }

  getApplicability(appkey: number) {
    var myurl = "/audithub/apptool/applicability/" + this.csskey + "/" + appkey;
    this.router.navigateByUrl(myurl).then(
      (nav) => {
        console.log("getApplicability() navigate to: " + myurl);
      },
      (err) => {
        console.log(err);
      },
    );
  }
}

/**
 * Dialog window to display the difference of rulebook after updating.
 */
@Component({
  selector: "displayReview",
  templateUrl: "review-update.html",
  styleUrls: ["./edit-template.component.css"],
  standalone: false,
})
export class DisplayReviewDialog implements OnInit {
  sections: SectContents[];
  sigOnly: boolean;
  showToggle: boolean;
  loading: boolean = false;
  // What each paragraph shows: the comparison (default), or only the new or the old version.
  contentView: "compare" | "new" | "old" = "compare";

  @ViewChild("reviewContent") reviewContent!: ElementRef;

  constructor(
    public dialogRef: MatDialogRef<DisplayReviewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ReviewDialogData,
    private service: TemplateService,
  ) {}
  ngOnInit() {
    this.sigOnly = this.data.sigOnly;
    // The toggle to switch between 'significant changes' and 'any change' only makes
    // sense when the dialog was opened from the significant-change review flow.
    this.showToggle = this.data.sigOnly;
    this.applySections(this.data.sections);
  }

  onViewChange(value: string): void {
    const wantSigOnly = value === "sig";
    if (wantSigOnly === this.sigOnly) return;

    this.sigOnly = wantSigOnly;
    this.loading = true;
    this.service
      .reviewUpdate(this.data.csskey, this.data.appkey, this.sigOnly)
      .subscribe(
        (res: SectContents[]) => {
          this.loading = false;
          this.applySections(res);
        },
        (err) => {
          this.loading = false;
          console.error(err);
        },
      );
  }

  applySections(sections: SectContents[]): void {
    sections.forEach((sect) => {
      sect.rbSectName = this.service.decodeHtml(sect.rbSectName) ?? "";
      // Both versions are kept (decoded) for the 'new/old version only' views; the comparison
      // is word by word but shown in the new version's own formatting (lists, paragraphs,
      // bold), with the changes marked in place - see diffHtml.
      sect.para.forEach((pa) => {
        pa.oldQuestion = this.decodeHtml(pa.oldQuestion);
        pa.newQuestion = this.decodeHtml(pa.newQuestion);
        pa.oldGuidenote = this.decodeHtml(pa.oldGuidenote);
        pa.newGuidenote = this.decodeHtml(pa.newGuidenote);
        pa.diffQuestion = diffHtml(pa.oldQuestion, pa.newQuestion);
        pa.diffGuidenote = diffHtml(pa.oldGuidenote, pa.newGuidenote);
      });
    });
    this.sections = sections;
  }

  onContentViewChange(value: "compare" | "new" | "old"): void {
    this.contentView = value;
  }

  // The paragraph's question or guide note for the selected view.
  viewHtml(pa: ParaContents, field: "question" | "guidenote"): string {
    if (this.contentView === "new") {
      return field === "question" ? pa.newQuestion : pa.newGuidenote;
    }
    if (this.contentView === "old") {
      return field === "question" ? pa.oldQuestion : pa.oldGuidenote;
    }
    return field === "question" ? pa.diffQuestion : pa.diffGuidenote;
  }

  // A paragraph added in the new version has no old text, and one removed has no new text.
  isMissingInView(pa: ParaContents): boolean {
    if (this.contentView === "new") return !pa.newQuestion && !pa.newGuidenote;
    if (this.contentView === "old") return !pa.oldQuestion && !pa.oldGuidenote;
    return false;
  }

  onRemoveFlag(): void {
    this.dialogRef.close(true);
  }

  onClose(): void {
    this.dialogRef.close(null);
  }

  onPrint(): void {
    const content = this.reviewContent?.nativeElement?.innerHTML ?? "";
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    const html = `<!DOCTYPE html><html><head><title>Rulebook Update Review</title><style>
      body { font-family: "Helvetica Neue", sans-serif; }
      .sectionTitle { margin-top: 1em; }
      .para { margin-bottom: 1em; }
      del, ins {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        color-adjust: exact;
      }
      del { background-color: palevioletred; }
      ins { background-color: yellowgreen; text-decoration: none; }
    </style></head><body>${content}
    <script>window.onload = function() { window.print(); window.addEventListener('afterprint', function() { window.close(); }); };</script>
    </body></html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }

  diffString(o, n): string {
    o = o.replace(/\s+$/, "");
    n = n.replace(/\s+$/, "");

    var out = this.diff(
      o == "" ? [] : o.split(/\s+/),
      n == "" ? [] : n.split(/\s+/),
    );
    var str = "";

    var oSpace = o.match(/\s+/g);
    if (oSpace == null) {
      oSpace = ["\n"];
    } else {
      oSpace.push("\n");
    }
    var nSpace = n.match(/\s+/g);
    if (nSpace == null) {
      nSpace = ["\n"];
    } else {
      nSpace.push("\n");
    }

    if (out.n.length == 0) {
      for (var i = 0; i < out.o.length; i++) {
        str += "<del>" + out.o[i] + oSpace[i] + "</del>";
      }
    } else {
      if (out.n[0].text == null) {
        for (n = 0; n < out.o.length && out.o[n].text == null; n++) {
          str += "<del>" + out.o[n] + oSpace[n] + "</del>";
        }
      }

      for (var i = 0; i < out.n.length; i++) {
        if (out.n[i].text == null) {
          str += "<ins>" + out.n[i] + nSpace[i] + "</ins>";
        } else {
          var pre = "";

          for (
            n = out.n[i].row + 1;
            n < out.o.length && out.o[n].text == null;
            n++
          ) {
            pre += "<del>" + out.o[n] + oSpace[n] + "</del>";
          }
          str += " " + out.n[i].text + nSpace[i] + pre;
        }
      }
    }

    return str;
  }

  processHtml(html: string): string {
    var result = this.decodeHtml(html);
    return this.stripHtml(result);
  }

  decodeHtml(html: string): string {
    if (html == null) return "";
    var txt = document.createElement("textarea");
    txt.innerHTML = html;
    return txt.value;
  }

  stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, "");
  }

  diff(o, n) {
    var ns = new Object();
    var os = new Object();

    for (var i = 0; i < n.length; i++) {
      if (ns[n[i]] == null) ns[n[i]] = { rows: new Array(), o: null };
      ns[n[i]].rows.push(i);
    }

    for (var i = 0; i < o.length; i++) {
      if (os[o[i]] == null) os[o[i]] = { rows: new Array(), n: null };
      os[o[i]].rows.push(i);
    }

    for (var j in ns) {
      if (
        ns[j].rows.length == 1 &&
        typeof os[j] != "undefined" &&
        os[j].rows.length == 1
      ) {
        n[ns[j].rows[0]] = { text: n[ns[j].rows[0]], row: os[j].rows[0] };
        o[os[j].rows[0]] = { text: o[os[j].rows[0]], row: ns[j].rows[0] };
      }
    }

    for (var i = 0; i < n.length - 1; i++) {
      if (
        n[i].text != null &&
        n[i + 1].text == null &&
        n[i].row + 1 < o.length &&
        o[n[i].row + 1].text == null &&
        n[i + 1] == o[n[i].row + 1]
      ) {
        n[i + 1] = { text: n[i + 1], row: n[i].row + 1 };
        o[n[i].row + 1] = { text: o[n[i].row + 1], row: i + 1 };
      }
    }

    for (var i = n.length - 1; i > 0; i--) {
      if (
        n[i].text != null &&
        n[i - 1].text == null &&
        n[i].row > 0 &&
        o[n[i].row - 1].text == null &&
        n[i - 1] == o[n[i].row - 1]
      ) {
        n[i - 1] = { text: n[i - 1], row: n[i].row - 1 };
        o[n[i].row - 1] = { text: o[n[i].row - 1], row: i - 1 };
      }
    }

    return { o: o, n: n };
  }
}

@Component({
  selector: "confirm-dialog",
  templateUrl: "confirm-dialog.html",
  styleUrls: ["./edit-template.component.css"],
  standalone: false,
})
export class ConfirmBuildDialog implements OnInit {
  unlockFlag?: boolean;
  build: string = "";
  buildType: string = "";

  constructor(
    public dialogRef: MatDialogRef<ConfirmBuildDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { build: string },
  ) {}

  ngOnInit() {
    this.build = this.data.build;
    if (this.build === "X3.0") {
      this.unlockFlag = false;
      this.buildType = "Build Xcelerator";
    } else if (this.build === "C3.0") {
      this.buildType = "Build Applicable Citations";
    } else if (this.build === "csv") {
      this.buildType = "Build CSV";
    } else if (this.build === "reghub") {
      this.buildType = "Monitor RegRegister";
    } else {
      this.buildType = "Build " + this.build;
    }
  }

  onConfirm(): void {
    if (this.unlockFlag === undefined) {
      this.dialogRef.close(0);
      return;
    }
    if (this.unlockFlag === false) {
      this.dialogRef.close(1);
      return;
    }
    this.dialogRef.close(2); // unlock the xcelerator.
  }

  onCancel(): void {
    this.dialogRef.close(-1);
  }

  onCheckboxClick(evt$) {
    this.unlockFlag = evt$.checked;
  }
}

/**
 * Dialog window to display the state list.
 */
@Component({
  selector: "state-list",
  templateUrl: "state-list.html",
  styleUrls: ["./edit-template.component.css"],
  standalone: false,
})
export class StateListDialog implements OnInit {
  stateChecked: string[];
  tips: string = "";
  unlockFlag?: boolean;
  build: string = "";
  buildType: string = "";

  constructor(
    public dialogRef: MatDialogRef<StateListDialog>,
    @Inject(MAT_DIALOG_DATA)
    public data: { selected: string; build: string; states: string[] },
  ) {}

  ngOnInit() {
    this.stateChecked = [];
    if (this.data.build == "X3.0") {
      this.unlockFlag = false;
    }

    if (this.data.selected.length > 0) {
      var splitted = this.data.selected.split(",");
      splitted.forEach((x) => this.stateChecked.push(x));
    }
    if (this.data.build == "X3.0" || this.data.build == "csv") {
      this.tips =
        "*Please note when selecting multiple states you will receive separate files for each state.";
    } else if (this.data.build == "C3.0") {
      this.tips =
        "*Please note when selecting multiple states you will receive one combined file.";
    } else if (this.data.build == "reghub") {
      this.tips =
        "*Please note when selecting multiple states a combined register will be sent to RegHub.";
    } else if (this.data.build == "regcompare") {
      this.tips = "*Please choose up to <b>FIVE</b> jurisdictions to compare.";
    } else {
      this.tips =
        "*Please note for efficient Partner Integration, select one state only to include in your template. If other jurisdictions are needed, please copy this template and select the additional states one at a time.";
    }

    this.build = this.data.build;
    if (this.build === "X3.0") {
      this.unlockFlag = false;
      this.buildType = "Build Xcelerator";
    } else if (this.build === "C3.0") {
      this.buildType = "Build Applicable Citations";
    } else if (this.build === "csv") {
      this.buildType = "Build CSV";
    } else if (this.build === "reghub") {
      this.buildType = "Monitor RegRegister";
    } else if (this.data.build === "regcompare") {
      this.buildType = "Jurisdictions to Compare";
    } else {
      this.buildType = "Build " + this.build;
    }
  }

  onConfirm(): void {
    if (this.unlockFlag !== undefined) {
      this.stateChecked.push(this.unlockFlag ? "1" : "0");
    }
    this.dialogRef.close(this.stateChecked);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  onStateClick(evt$, state: string) {
    if (evt$.checked) {
      this.stateChecked.push(state);
    } else {
      const index = this.stateChecked.indexOf(state);
      if (index > -1) {
        this.stateChecked.splice(index, 1);
      }
    }
  }

  onUnlockClick(evt$) {
    this.unlockFlag = evt$.checked;
  }
}

/**
 * Dialog window to display the PreAnswerList Dialog.
 */
@Component({
  selector: "preAnswer",
  templateUrl: "preAnswer.html",
  styleUrls: ["./edit-template.component.css"],
  standalone: false,
})
export class PreAnswerListDialog implements OnInit {
  answerSelected = "";
  tips: string;

  constructor(
    public dialogRef: MatDialogRef<PreAnswerListDialog>,
    @Inject(MAT_DIALOG_DATA) public data: { answers: string[] },
  ) {}

  ngOnInit() {}

  onConfirm(): void {
    if (this.answerSelected.length === 0) {
      alert("Please choose the predefined answer code for Intelex.");
      return;
    }
    this.dialogRef.close(this.answerSelected);
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  radioChange(event: MatRadioChange) {
    this.answerSelected = event.value;
  }
}
